import React, { useEffect, useState } from "react";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import MetricsGrid from "@/components/dashboard/MetricsGrid";
import RecentActivity from "@/components/dashboard/RecentActivity";
import QuickActions from "@/components/dashboard/QuickActions";
import InsightsSection from "@/components/dashboard/InsightsSection";
import { ProjectsList } from "@/components/dashboard/ProjectsList";
import { UsageQuotaWidget } from "@/components/dashboard/UsageQuotaWidget";
import { supabase } from "@/integrations/supabase/client";
import MainLayout from '@/components/layout/MainLayout';
import { EnhancedDashboardAI } from "@/components/ai/EnhancedDashboardAI";
import { GoalsOverview } from "@/components/goals/GoalsOverview";
import { identifyUser } from "@/lib/analytics";
import { EmptyDashboard } from "@/components/dashboard/EmptyDashboard";
import { SEO } from "@/components/seo/SEO";
import { IntroVideo } from "@/components/dashboard/IntroVideo";

const Index = () => {
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [companyName, setCompanyName] = useState<string | undefined>();

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        identifyUser(user.id, { email: user.email });

        // Check if user has data
        const [importsRes, sourcesRes, runsRes, profileRes] = await Promise.all([
          supabase.from("data_imports").select("id", { count: "exact", head: true }).eq("user_id", user.id),
          supabase.from("data_sources").select("id", { count: "exact", head: true }).eq("user_id", user.id),
          supabase.from("workflow_runs").select("id", { count: "exact", head: true }).eq("user_id", user.id),
          supabase.from("profiles").select("company_name").eq("id", user.id).single(),
        ]);

        const totalData = (importsRes.count ?? 0) + (sourcesRes.count ?? 0) + (runsRes.count ?? 0);
        setHasData(totalData > 0);
        setCompanyName(profileRes.data?.company_name ?? undefined);

        // Onboarding tour is owned by MainLayout via useOnboarding.
      } catch {
        setHasData(false);
      }
    };
    init();
  }, []);

  return (
    <MainLayout>
      <SEO title="Dashboard — Veylo AI" description="Your AI-powered analytics dashboard with predictions, insights, and workflows." path="/" />
      <DashboardHeader />

      {hasData === null ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
        </div>
      ) : hasData === false ? (
        <EmptyDashboard companyName={companyName} />
      ) : (
        <div className="space-y-6">
          <QuickActions />
          <EnhancedDashboardAI />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <MetricsGrid />
              <RecentActivity />
              <InsightsSection />
              <GoalsOverview />
            </div>
            <div className="space-y-8">
              <IntroVideo />
              <UsageQuotaWidget />
              <ProjectsList />
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Index;
